import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { GridApi, GridPreDestroyedEvent } from 'ag-grid-community';

/**
 * Close a grid's floating popups: the "Choose Columns" dialog and any open
 * column or context menu. The grids render popups into document.body
 * (popupParent), and AG Grid 32 does not destroy an open column chooser dialog
 * with the grid, so without this it stays on screen after the user navigates
 * to another page.
 */
export function closeGridPopups(api: GridApi | null | undefined): void {
  if (!api || api.isDestroyed()) return;
  api.hideColumnChooser();
  api.hidePopupMenu();
}

/**
 * Closes the grid's popups when the grid is destroyed and when the route's
 * path changes while the grid stays mounted (one page component serving
 * several paths). Pass the returned handler as the grid's onGridPreDestroyed.
 */
export function useGridPopupCleanup(getApi: () => GridApi | null | undefined): (event: GridPreDestroyedEvent) => void {
  const { pathname } = useLocation();
  const getApiRef = useRef(getApi);
  getApiRef.current = getApi;
  const firstPath = useRef(pathname);
  useEffect(() => {
    if (firstPath.current === pathname) return;
    firstPath.current = pathname;
    closeGridPopups(getApiRef.current());
  }, [pathname]);
  return useCallback((event: GridPreDestroyedEvent) => closeGridPopups(event.api), []);
}
